import React, { useState, useContext, useRef, useMemo, useCallback, useEffect } from 'react';

import { Card, MessageFeed } from '@ergeon/core-components';

import './UnifiedDialog.css';

import { markUnifiedDialogAsRead } from '../../../api';
import { CustomerContext } from '../../../context-providers/CustomerContext';
import { useUnifiedDialog } from '../../../context-providers/UnifiedDialogContext';
import { useCentrifugoNotifications } from '../../../hooks/useCentrifugoNotifications';
import useNotificationSound from '../../../hooks/useNotificationSound';
import usePageTitleNotification from '../../../hooks/usePageTitleNotification';
import { MessageChannel } from '../../../routes';
import { UnifiedDialogEntry } from '../../../types';

import { convertEntryToMessage } from './utils/convertEntryToMessage';
import { injectUnreadDivider } from './utils/injectUnreadDivider';
import { useUnifiedDialogPagination } from './hooks/useUnifiedDialogPagination';
import { useFilteredMessages } from './hooks/useFilteredMessages';
import { useSortedEntries } from './hooks/useSortedEntries';
import { useSearchFilteredMessages } from './hooks/useSearchFilteredMessages';
import { useDialogUrlParams } from './hooks/useDialogUrlParams';
import { useScrollToBottom } from './hooks/useScrollToBottom';
import { createContactValidator } from './validation/contactValidator';
import type { ComposeToolbarType } from './components/InputTypes/types';
import DialogFilters from './components/DialogFilters';
import ComposeInputRenderer from './components/ComposeInputRenderer';
import ComposeToolbar from './components/ComposeToolbar';

const UnifiedDialog: React.FC = () => {
  const { selectedCustomer, customerDetail, setCustomerDetail } = useContext(CustomerContext);
  const customerId = selectedCustomer?.id;
  const { resetIncrementalCount } = useUnifiedDialog();
  const { params, clearParams } = useDialogUrlParams();
  const notificationSound = useNotificationSound();
  const titleNotification = usePageTitleNotification();

  // Initialize filter from URL param (message_channel=sms sets filter to SMS)
  const getInitialFilter = () => (params.messageChannel === MessageChannel.SMS ? 'SMS' : 'All');

  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState(getInitialFilter);
  const [showUnreads, setShowUnreads] = useState<'all' | 'unreads'>('all');
  const [replySubject, setReplySubject] = useState<string | null>(null);

  // ComposeType state for toolbar - initialize from URL params or default to Notes
  const [composeType, setComposeType] = useState<ComposeToolbarType>(() => {
    // URL params take priority
    if (params.messageChannel === MessageChannel.SMS) {
      return 'SMS';
    }
    // Default to 'Notes' (APPS-6815)
    return 'Notes';
  });

  // Validate 'to' param against customer contacts
  const toValidation = useMemo(() => {
    if (!params.to) return { isValid: true, error: null, validatedTo: null };

    if (!customerDetail) {
      return { isValid: false, error: 'Loading customer contacts...', validatedTo: null };
    }

    const validator = createContactValidator(customerDetail.primary_phone, customerDetail.additional_phones);

    const isValid = validator.isValidPhone(params.to);
    return {
      isValid,
      error: isValid ? null : `Phone number ${params.to} is not associated with this customer`,
      validatedTo: isValid ? validator.normalizePhone(params.to) : null,
    };
  }, [params.to, customerDetail]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevActiveFilterRef = useRef(activeFilter);

  // Optimistic messages state (declared early for useScrollToBottom dependencies)
  const [optimisticMessages, setOptimisticMessages] = useState<UnifiedDialogEntry[]>([]);

  const { entries, lastReadAt, isLoading, handleLoadMore, refetch } = useUnifiedDialogPagination({
    customerId,
    scrollContainerRef,
  });

  const { triggerScrollToBottom } = useScrollToBottom({
    scrollContainerRef,
    dependencies: [entries, optimisticMessages],
  });

  // Sync composeType when activeFilter changes from DialogFilters
  // Only sync if activeFilter actually changed (not just re-rendered)
  useEffect(() => {
    // Skip if activeFilter hasn't actually changed
    if (activeFilter === prevActiveFilterRef.current) {
      return;
    }

    // Update ref to track current value
    prevActiveFilterRef.current = activeFilter;

    let newType: ComposeToolbarType;
    if (activeFilter === 'SMS') {
      newType = 'SMS';
    } else if (activeFilter === 'Emails') {
      newType = 'Email';
    } else {
      newType = 'Notes'; // Default for All/Calls/etc
    }

    setComposeType(newType);
  }, [activeFilter]);

  // Handler for toolbar type changes
  const handleComposeTypeChange = useCallback((newType: ComposeToolbarType) => {
    setComposeType(newType);

    // Update activeFilter when toolbar changes composeType
    if (newType === 'SMS') {
      setActiveFilter('SMS');
    } else if (newType === 'Email') {
      setActiveFilter('Emails');
    }
    // For 'Notes' or null, don't change activeFilter
  }, []);

  // Callback to clear URL params after successful SMS send
  const handleSmsSent = useCallback(async() => {
    clearParams();
    await refetch();
    // Scroll after API response
    requestAnimationFrame(() => {
      triggerScrollToBottom();
    });
  }, [clearParams, triggerScrollToBottom, refetch]);

  // Callback to scroll to bottom after successful email/note send
  const handleMessageSent = useCallback(async() => {
    await refetch();
    // Scroll after API response
    requestAnimationFrame(() => {
      triggerScrollToBottom();
    });
    setReplySubject(null); // Clear reply subject after send
  }, [triggerScrollToBottom, refetch]);

  // Callback for replying to an email message
  const handleReply = useCallback(
    (subject: string) => {
      setReplySubject(subject);
      setComposeType('Email');
      setActiveFilter('Emails');
    },
    [],
  );

  // Callback to mark all messages as read
  const handleMarkRead = useCallback(async() => {
    if (!customerId) return;
    try {
      const response = await markUnifiedDialogAsRead(customerId);
      resetIncrementalCount(customerId);
      if (customerDetail) {
        setCustomerDetail({ ...customerDetail, ud_unread_count: response.unread_count });
      }
      refetch();
    } catch {
      // Silently handle error - user can retry via button
    }
  }, [customerId, customerDetail, refetch, resetIncrementalCount, setCustomerDetail]);

  // Optimistic message management
  const addOptimisticMessage = useCallback((entry: UnifiedDialogEntry) => {
    setOptimisticMessages((prev) => [...prev, entry]);
    triggerScrollToBottom();
  }, [triggerScrollToBottom]);

  const updateOptimisticMessage = useCallback((optimisticId: string, updates: Partial<UnifiedDialogEntry>) => {
    setOptimisticMessages((prev) =>
      prev.map((msg) => (msg.optimisticId === optimisticId ? { ...msg, ...updates } : msg)),
    );
  }, []);

  const removeOptimisticMessage = useCallback((optimisticId: string) => {
    setOptimisticMessages((prev) => prev.filter((msg) => msg.optimisticId !== optimisticId));
  }, []);

  const handleDialogueUpdated = useCallback(
    (notificationCustomerId: string) => {
      if (customerId && String(customerId) === notificationCustomerId) {
        refetch();

        // Play notification sound
        notificationSound.play();

        // Update page title
        titleNotification.notify(selectedCustomer?.full_name || 'Customer');
      }
    },
    [customerId, refetch, notificationSound, titleNotification, selectedCustomer],
  );

  useCentrifugoNotifications({ onDialogueUpdated: handleDialogueUpdated });

  // Merge optimistic messages with real entries
  const allEntries = useMemo(() => [...entries, ...optimisticMessages], [entries, optimisticMessages]);

  const filteredEntries = useFilteredMessages(allEntries, activeFilter);
  const sortedEntries = useSortedEntries(filteredEntries);
  const messages = sortedEntries.map((entry) => convertEntryToMessage(entry, { onReply: handleReply }));
  const messagesWithDivider = injectUnreadDivider(messages, sortedEntries, lastReadAt, handleMarkRead, customerDetail?.ud_unread_count);
  const filteredMessages = useSearchFilteredMessages(messagesWithDivider, searchText);

  return (
    <Card className="flex flex-col flex-grow gap-3 p-4 w-full overflow-hidden" flavor="base" zIndex="1">
      <DialogFilters
        activeFilter={activeFilter}
        entries={entries}
        searchText={searchText}
        setActiveFilter={setActiveFilter}
        setSearchText={setSearchText}
        setShowUnreads={setShowUnreads}
        showUnreads={showUnreads} />
      <Card
        className="flex flex-col flex-1 justify-center px-4 overflow-hidden"
        flavor="regular"
        ref={scrollContainerRef}>
        <MessageFeed isLoading={isLoading} messages={filteredMessages} onScroll={handleLoadMore} />
      </Card>
      <div className="z-20 flex justify-center items-center -mt-6 -mb-2 w-full">
        <ComposeToolbar activeType={composeType} onTypeChange={handleComposeTypeChange} />
      </div>

      <ComposeInputRenderer
        composeType={composeType}
        customerEmail={selectedCustomer?.primary_email?.identifier}
        customerId={customerId}
        customerName={selectedCustomer?.full_name}
        customerPhone={toValidation.validatedTo ||
          selectedCustomer?.phone_number ||
          customerDetail?.additional_phones?.[0]?.identifier}
        hasPermanentPhone={customerDetail?.primary_phone != null && !customerDetail.primary_phone.is_temporary}
        initialSubject={replySubject}
        initialText={params.text}
        onAddOptimisticMessage={addOptimisticMessage}
        onRemoveOptimisticMessage={removeOptimisticMessage}
        onSmsSent={handleSmsSent}
        onSuccess={handleMessageSent}
        onUpdateOptimisticMessage={updateOptimisticMessage}
        temporaryPhone={customerDetail?.primary_phone?.is_temporary ? customerDetail.primary_phone.identifier : null}
        toValidationError={toValidation.error}
        yelpLeadKey={customerDetail?.yelp_lead_key} />
    </Card>
  );
};

export default UnifiedDialog;
